import { useContext, useMemo, useState, useEffect } from "react";
import { PageLoader } from "@remo-co/ui-core/src/components/PageLoader";
import "./JoinedEvents.scss";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { useI18n } from "i18n";
import { useSelector } from "react-redux";
import { usePopulateEventData } from "modules/event/hooks/apiHooks";
import EventListToggle from "modules/event/eventList/EventListToggle";
import {
  selectOngoingJoinedEvents,
  selectUpcomingJoinedEvents,
} from "modules/event/selectors";
import { IEvent } from "modules/event/types";
import { usePastJoinedEventsQuery } from "modules/event/hooks";

interface Props {
  ongoingJoinedEvents: IEvent[];
  upcomingJoinedEvents: IEvent[];
  isLoading: boolean;
}

export const JoinedEventContainerComponent = ({
  ongoingJoinedEvents,
  upcomingJoinedEvents,
  isLoading,
}: Props) => {
  const {
    data: infinitePastJoinedEvents,
    fetchNextPage,
    hasNextPage,
    isFetching: isLoadingPastEvents,
    refetch: refetchPastEvents,
    totalCount,
  } = usePastJoinedEventsQuery();
  const [pastJoinedEvents, setPastJoinedEvents] = useState<IEvent[]>([]);
  const { t } = useI18n(["common", "event"]);

  useMemo(() => {
    const events = infinitePastJoinedEvents?.pages.reduce(
      (union, e) => union.concat(e),
      [],
    );

    setPastJoinedEvents(events ?? []);
  }, [infinitePastJoinedEvents?.pages]);

  return (
    <div>
      <EventListToggle
        title={t("event:ongoing.joined.event")}
        events={ongoingJoinedEvents}
        editable={false}
      />
      <EventListToggle
        title={t("event:future.joined.event")}
        events={upcomingJoinedEvents}
        editable={false}
      />
      <EventListToggle
        title={t("event:past.joined.event")}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isLoading={isLoadingPastEvents}
        events={pastJoinedEvents}
        refetch={refetchPastEvents}
        totalCount={totalCount}
        editable={false}
      />

      {isLoading && <PageLoader message={t("event:event.loading")} />}
    </div>
  );
};

const JoinedEventContainer = () => {
  const ongoingJoinedEvents = useSelector(selectOngoingJoinedEvents);
  const upcomingJoinedEvents = useSelector(selectUpcomingJoinedEvents);
  const { getJoinedEvents } = usePopulateEventData();
  const [isLoading, setIsLoading] = useState(true);
  const { track } = useContext(TRACKING_CONTEXT);

  useEffect(() => {
    setIsLoading(true);
    // eslint-disable-next-line promise/catch-or-return, promise/prefer-await-to-then
    getJoinedEvents().finally(() => {
      setIsLoading(false);
      track(Events.JOINED_EVENTS_VIEWED);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <JoinedEventContainerComponent
      ongoingJoinedEvents={ongoingJoinedEvents}
      upcomingJoinedEvents={upcomingJoinedEvents}
      isLoading={isLoading}
    />
  );
};

export default JoinedEventContainer;
